/**
 * billingView — pure selector mapping the server-computed ``BillingState`` to
 * the component-facing ``BillingView`` (camelCase) the ported design uses.
 *
 * IMPORTANT: this never recomputes lock/trial business logic. The server owns
 * ``locked``, ``is_free``, ``trial_no_card`` etc.; this only renames them and
 * resolves the plan id to a normalized ``PlanId``. A server-side bug fix in
 * the view propagates here without a code change.
 */

import { planById } from "./plans";
import type { BillingState, BillingView, PlanId } from "./types";

function normalizePlanId(id: string | null): PlanId {
  return id === "single" || id === "family" ? id : planById(id).id;
}

export function billingView(state: BillingState): BillingView {
  return {
    status: state.status,
    planId: normalizePlanId(state.plan_id),
    kind: state.kind,
    consent: state.consent,
    daysLeft: state.days_left,
    endsOn: state.ends_on ?? null,
    locked: state.locked,
    isFree: state.is_free,
    lapsed: state.lapsed,
    inTrial: state.in_trial,
    canCreate: state.can_create,
    trialNoCard: state.trial_no_card,
    hasCard: state.has_card,
    card: state.card_on_file,
    dunningStage: state.dunning_stage ?? null,
    dunningDue: state.dunning_due ?? null,
    seated: state.seated ?? true,
    seats: state.seats
      ? { count: state.seats.count, holderIds: state.seats.holder_ids, choicePending: state.seats.choice_pending }
      : null,
    pendingPlanId: state.pending_plan_id ? normalizePlanId(state.pending_plan_id) : null,
    pendingOn: state.pending_on ?? null,
  };
}
